import React, { useEffect, useState } from "react";
import { Smartphone, Share, Plus, Home, Check, X } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

// "Add GlobalEyes.TV to your home screen" prompt — encourages users to
// install the PWA so the site behaves like a native app icon. Because
// Apple disabled the programmatic install prompt on iOS, we have to
// fall back to a 3-step illustrated guide for iPhone/iPad users.
// Android Chrome / Edge still expose the `beforeinstallprompt` event,
// so on those devices we trigger the OS-native install sheet directly.
//
// Trigger points (controlled by the parent):
//   - Right after a successful registration (primary moment)
//   - Voluntarily from the user dropdown menu (so they can find it later)
//
// We persist `installPromptDismissed=true` in localStorage so we never
// re-nag a user who's already installed (or explicitly declined).

const DISMISS_KEY = "globaleyes_install_dismissed";

// --- Platform detection ----------------------------------------------------
const platformHint = () => {
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /android/i.test(ua);
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
  // Identify the engine so we can show install instructions that
  // actually match the browser the user is on. Desktop Safari and
  // desktop Firefox have NO install option (Apple/Mozilla disabled
  // PWA install on those engines), so showing "three-dots menu" steps
  // is misleading. Order matters: Chrome's UA also contains "Safari",
  // so we must rule Chromium-based browsers out first.
  const isChromium = /Chrome|CriOS|Edg/.test(ua) && !/OPR\//.test(ua);
  const isFirefox = /Firefox|FxiOS/.test(ua);
  const isSafari = /Safari/.test(ua) && !isChromium && !isFirefox;
  const isDesktop = !isIOS && !isAndroid;
  // Desktop Safari + desktop Firefox cannot install PWAs — surface
  // that explicitly so we don't tell users to find menu options
  // their browser doesn't have.
  const cannotInstall = isDesktop && (isSafari || isFirefox);
  return { isIOS, isAndroid, isStandalone, isChromium, isFirefox, isSafari, isDesktop, cannotInstall };
};

// Module-level cache of the beforeinstallprompt event. We capture it as
// soon as the browser fires it (well before the user opens the dialog)
// so it's ready to call .prompt() when they tap Install.
let deferredInstallEvent = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallEvent = e;
  });
  window.addEventListener("appinstalled", () => {
    deferredInstallEvent = null;
    try { localStorage.setItem(DISMISS_KEY, "installed"); } catch { /* ignore */ }
  });
}

// Exported helper any component can call ("Install app" link in the
// dropdown, etc.). Returns true if a prompt was actually shown.
export const openInstallPrompt = () => {
  window.dispatchEvent(new CustomEvent("globaleyes:open-install-prompt"));
};

export const shouldShowAutoPrompt = () => {
  try {
    if (localStorage.getItem(DISMISS_KEY)) return false;
  } catch { return false; }
  const p = platformHint();
  if (p.isStandalone) return false; // already installed
  // Desktop Safari / Firefox can't install PWAs — auto-prompting them
  // is a dead end. Mobile users always get the prompt.
  if (p.cannotInstall) return false;
  return true;
};

const InstallPromptDialog = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("intro");
  const [platform, setPlatform] = useState({ isIOS: false, isAndroid: false, isStandalone: false });

  useEffect(() => {
    setPlatform(platformHint());
    const listener = () => {
      setStep("intro");
      setOpen(true);
    };
    window.addEventListener("globaleyes:open-install-prompt", listener);
    return () => window.removeEventListener("globaleyes:open-install-prompt", listener);
  }, []);

  const dismissForever = () => {
    try { localStorage.setItem(DISMISS_KEY, "dismissed"); } catch { /* ignore */ }
    setOpen(false);
  };

  const remindLater = () => {
    // Soft dismiss — DO NOT write the localStorage flag. Next time the
    // parent auto-opens this dialog (e.g. fresh registration), it'll
    // come back.
    setOpen(false);
  };

  // Try the native Android install sheet. If the browser never fired
  // beforeinstallprompt (Safari, Firefox iOS, etc.) we fall back to the
  // manual instructions step.
  const tryNativeInstall = async () => {
    if (deferredInstallEvent) {
      deferredInstallEvent.prompt();
      const choice = await deferredInstallEvent.userChoice;
      deferredInstallEvent = null;
      if (choice.outcome === "accepted") {
        setStep("confirm");
      } else {
        remindLater();
      }
    } else {
      // No native prompt available — pick the right illustrated path.
      // Desktop Safari/Firefox get a "use your phone instead" screen
      // because there's literally no install option in those browsers.
      if (platform.cannotInstall) {
        setStep("desktop-no-install");
      } else if (platform.isIOS) {
        setStep("ios-steps");
      } else {
        setStep("manual-steps");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) remindLater(); }}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white w-[95vw] max-w-md p-5" data-testid="install-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Smartphone className="h-5 w-5 text-emerald-400" />
            Put GlobalEyes.TV on your home screen
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-xs">
            One tap and you'll have a real app icon — no app-store download required.
          </DialogDescription>
        </DialogHeader>

        {step === "intro" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shrink-0">
                <Home className="h-6 w-6 text-white" />
              </div>
              <div className="text-sm">
                <p className="text-white font-medium">Faster access</p>
                <p className="text-gray-400 text-xs">Tap once from your home screen. No browser, no URL typing.</p>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                onClick={tryNativeInstall}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="install-prompt-go"
              >
                Show me how
              </Button>
              <button
                onClick={remindLater}
                className="text-gray-400 hover:text-white text-xs py-1"
                data-testid="install-prompt-later"
              >
                Maybe later
              </button>
              <button
                onClick={dismissForever}
                className="text-gray-600 hover:text-gray-400 text-[10px] py-1"
                data-testid="install-prompt-dismiss"
              >
                Don't ask again
              </button>
            </div>
          </div>
        )}

        {step === "ios-steps" && (
          <div className="space-y-3" data-testid="install-prompt-ios">
            <p className="text-gray-300 text-sm">Three quick steps on iPhone or iPad:</p>
            <ol className="space-y-2">
              <li className="flex items-start gap-3 bg-gray-800/60 rounded-lg p-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-gray-900 text-xs font-bold flex items-center justify-center">1</span>
                <div className="text-sm text-gray-200 flex-1">
                  Tap the <Share className="inline h-4 w-4 mx-1 text-emerald-300" /> <strong>Share</strong> button at the bottom of Safari.
                </div>
              </li>
              <li className="flex items-start gap-3 bg-gray-800/60 rounded-lg p-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-gray-900 text-xs font-bold flex items-center justify-center">2</span>
                <div className="text-sm text-gray-200 flex-1">
                  Scroll down and tap <Plus className="inline h-4 w-4 mx-1 text-emerald-300" /> <strong>Add to Home Screen</strong>.
                </div>
              </li>
              <li className="flex items-start gap-3 bg-gray-800/60 rounded-lg p-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-gray-900 text-xs font-bold flex items-center justify-center">3</span>
                <div className="text-sm text-gray-200 flex-1">
                  Tap <strong>Add</strong> in the top right. Done!
                </div>
              </li>
            </ol>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-200/90">
              <strong>Don't see Share?</strong> You're probably in Chrome or another browser on iPhone — Apple only lets Safari do this. Open this page in Safari and try again.
            </div>
            <Button onClick={() => setStep("confirm")} className="bg-emerald-600 hover:bg-emerald-500 text-white w-full" data-testid="install-prompt-ios-done">
              I've added it
            </Button>
          </div>
        )}

        {step === "manual-steps" && (
          <div className="space-y-3" data-testid="install-prompt-manual">
            <p className="text-gray-300 text-sm">In Chrome, Edge, or Brave on this device:</p>
            <ol className="space-y-2">
              <li className="flex items-start gap-3 bg-gray-800/60 rounded-lg p-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-gray-900 text-xs font-bold flex items-center justify-center">1</span>
                <div className="text-sm text-gray-200 flex-1">
                  Look in the <strong>address bar on the right</strong> for a small monitor / install icon, OR open the <strong>browser menu</strong> (⋮ on Android, ⋮ in Chrome desktop top-right).
                </div>
              </li>
              <li className="flex items-start gap-3 bg-gray-800/60 rounded-lg p-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-gray-900 text-xs font-bold flex items-center justify-center">2</span>
                <div className="text-sm text-gray-200 flex-1">Tap <strong>Install app</strong>, <strong>Install GlobalEyes.TV</strong>, or <strong>Add to Home Screen</strong>.</div>
              </li>
              <li className="flex items-start gap-3 bg-gray-800/60 rounded-lg p-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-gray-900 text-xs font-bold flex items-center justify-center">3</span>
                <div className="text-sm text-gray-200 flex-1">Confirm.</div>
              </li>
            </ol>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-200/90">
              <strong>On Safari or Firefox?</strong> Those browsers don't support installing this site — switch to Chrome, Edge, or Brave (or open this page on your phone).
            </div>
            <Button onClick={() => setStep("confirm")} className="bg-emerald-600 hover:bg-emerald-500 text-white w-full" data-testid="install-prompt-manual-done">
              I've added it
            </Button>
          </div>
        )}

        {step === "desktop-no-install" && (
          <div className="space-y-3" data-testid="install-prompt-no-install">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-sm text-amber-200/90">
              <strong>Heads up:</strong> {platform.isSafari ? "Safari" : "Firefox"} on desktop doesn't support installing GlobalEyes.TV as an app.
            </div>
            <p className="text-gray-300 text-sm">Here's what you can do instead:</p>
            <ol className="space-y-2">
              <li className="flex items-start gap-3 bg-gray-800/60 rounded-lg p-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-gray-900 text-xs font-bold flex items-center justify-center">A</span>
                <div className="text-sm text-gray-200 flex-1">
                  <strong>Use your phone</strong> — open <span className="text-emerald-300">globaleyes.tv</span> on iPhone (Safari) or Android (Chrome) and you'll see the install prompt there.
                </div>
              </li>
              <li className="flex items-start gap-3 bg-gray-800/60 rounded-lg p-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-gray-900 text-xs font-bold flex items-center justify-center">B</span>
                <div className="text-sm text-gray-200 flex-1">
                  <strong>Switch desktop browser</strong> — open this page in Chrome, Edge, or Brave and they'll offer to install it.
                </div>
              </li>
              <li className="flex items-start gap-3 bg-gray-800/60 rounded-lg p-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-gray-900 text-xs font-bold flex items-center justify-center">C</span>
                <div className="text-sm text-gray-200 flex-1">
                  <strong>Bookmark this page</strong> for now and grab it from your bookmarks bar — same one-click access.
                </div>
              </li>
            </ol>
            <Button onClick={dismissForever} className="bg-emerald-600 hover:bg-emerald-500 text-white w-full" data-testid="install-prompt-no-install-done">
              Got it
            </Button>
          </div>
        )}

        {step === "confirm" && (
          <div className="space-y-3 text-center" data-testid="install-prompt-confirm">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 mx-auto flex items-center justify-center">
              <Check className="h-8 w-8 text-emerald-400" />
            </div>
            <p className="text-white text-base font-medium">Look at your home screen.</p>
            <p className="text-gray-400 text-sm">Do you see the GlobalEyes.TV icon now?</p>

            <div className="flex flex-col gap-2 pt-2">
              <Button onClick={dismissForever} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="install-prompt-confirmed-yes">
                <Check className="h-4 w-4 mr-1" /> Yes, all set
              </Button>
              <button
                onClick={() => setStep(platform.isIOS ? "ios-steps" : "manual-steps")}
                className="text-gray-400 hover:text-white text-xs py-1"
                data-testid="install-prompt-confirmed-no"
              >
                <X className="h-3 w-3 inline" /> Not yet — show me the steps again
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InstallPromptDialog;
